/**
 * <s-push-optin> — friendly "Get notifications" card shown before the browser's permission prompt.
 *
 * Shows only when all are true:
 * - the browser supports web notifications (on iPhone this means the store is installed to the home screen)
 * - permission has not been granted or blocked yet
 * - the visitor has viewed at least 2 pages (counted across visits)
 * - "Not now" was not tapped in the last 14 days
 * - the "Get the Vedavital app" card ([data-install-prompt]) is not on screen
 * Waits 8 seconds after page load so it never interrupts the first look.
 *
 * Allow asks the browser for notification permission; the push app (Pushbot app embed) then
 * signs the visitor up. This card sends nothing itself.
 *
 * Commands (commandfor this element's id): --allow, --dismiss
 */
const VIEWS_KEY = 'vv-push-views';
const DISMISSED_KEY = 'vv-push-dismissed';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const DELAY_MS = 8000;
const MIN_VIEWS = 2;

class SPushOptin extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    this.#ac = new AbortController();
    const views = this.#countView();
    if (!this.#supported() || Notification.permission !== 'default') return;
    if (this.#snoozed() || views < MIN_VIEWS) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--allow') this.#allow();
      if (event.command === '--dismiss') this.#dismiss();
    }, { signal: this.#ac.signal });

    this.#timer = setTimeout(() => this.#tryShow(), DELAY_MS);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #tryShow() {
    const installCard = document.querySelector('[data-install-prompt]');
    if (installCard && !installCard.hidden) {
      // Try again later so the two cards never stack.
      this.#timer = setTimeout(() => this.#tryShow(), DELAY_MS);
      return;
    }
    if (Notification.permission !== 'default') return;
    this.hidden = false;
  }

  async #allow() {
    this.hidden = true;
    try {
      await Notification.requestPermission();
    } catch {
      // Older browsers use a callback form; ignore failures.
    }
  }

  #dismiss() {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // Storage unavailable; hide for this page only.
    }
    this.hidden = true;
  }

  #supported() {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  #snoozed() {
    try {
      const at = Number(localStorage.getItem(DISMISSED_KEY));
      return Boolean(at) && Date.now() - at < SNOOZE_MS;
    } catch {
      return true;
    }
  }

  #countView() {
    try {
      const views = (Number(localStorage.getItem(VIEWS_KEY)) || 0) + 1;
      localStorage.setItem(VIEWS_KEY, String(views));
      return views;
    } catch {
      return 0;
    }
  }
}

if (!customElements.get('s-push-optin')) customElements.define('s-push-optin', SPushOptin);
